import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AppShell, Icon } from '../components/AppShell';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { fmtEUR } from '../lib/format';
import type { PricelistItem } from '@recotex/shared';

type SortKey = 'pdfNaam' | 'naamArbeid' | 'eenheid' | 'prijs';
type SortDir = 'asc' | 'desc';
const EENHEDEN = ['STUK', 'LM', 'M2', 'FORFAIT'] as const;

export function PricelistManager() {
  const qc = useQueryClient();
  const toast = useToast();
  // Prijslijsten en standaardprijzen beheren: enkel met beheerrecht (Ervin en Timen, SPEC 27okt 7).
  const { user } = useAuth();
  const magBeheren = !!user?.magBeheren;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [filterArbeid, setFilterArbeid] = useState('');
  const [hideNvt, setHideNvt] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('naamArbeid');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAdd, setShowAdd] = useState(false);

  const listQ = useQuery({ queryKey: ['pricelists'], queryFn: () => api.pricelists.list() });
  const detailQ = useQuery({
    queryKey: ['pricelist', activeId],
    queryFn: () => api.pricelists.detail(activeId!),
    enabled: !!activeId,
  });

  const pricelists = listQ.data?.pricelists ?? [];
  const active = detailQ.data?.pricelist;
  const versiebeheerd = !!active?.typeWerken && active.typeWerken !== 'algemeen';

  // Auto-select eerste prijslijst
  if (!activeId && pricelists.length > 0) setActiveId(pricelists[0]!.id);

  const updateItem = useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: Record<string, unknown> }) =>
      api.pricelists.updateItem(activeId!, itemId, data as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pricelist', activeId] }),
    onError: () => toast.error('Wijziging mislukt'),
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.pricelists.removeItem(activeId!, itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pricelist', activeId] }),
    onError: () => toast.error('Verwijderen mislukt'),
  });

  const addItem = useMutation({
    mutationFn: (data: Omit<PricelistItem, 'id' | 'pricelistId'>) =>
      api.pricelists.addItem(activeId!, data),
    onSuccess: () => {
      toast.success('Item toegevoegd');
      qc.invalidateQueries({ queryKey: ['pricelist', activeId] });
      setShowAdd(false);
    },
    onError: () => toast.error('Toevoegen mislukt'),
  });

  const allArbeid = active
    ? Array.from(new Set((active.items ?? []).map((it) => it.naamArbeid).filter(Boolean))).sort()
    : [];

  const filteredItems = useMemo(() => {
    const items = (active?.items ?? []).filter((it) => {
      if (hideNvt && (it.isNvt || it.naamArbeid === 'NVT')) return false;
      if (filterText) {
        const q = filterText.toLowerCase();
        if (!it.pdfNaam.toLowerCase().includes(q) && !it.naamArbeid.toLowerCase().includes(q)) return false;
      }
      if (filterArbeid && it.naamArbeid !== filterArbeid) return false;
      return true;
    });
    items.sort((a, b) => {
      const av: string | number = sortKey === 'prijs' ? a.prijs : (a[sortKey] || '');
      const bv: string | number = sortKey === 'prijs' ? b.prijs : (b[sortKey] || '');
      let cmp: number;
      if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv, 'nl');
      else cmp = (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [active?.items, filterText, filterArbeid, hideNvt, sortKey, sortDir]);

  const toggleSort = (col: SortKey) => {
    if (sortKey === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(col); setSortDir(col === 'prijs' ? 'desc' : 'asc'); }
  };

  // Selection helpers
  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every((it) => selected.has(it.id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) for (const it of filteredItems) next.delete(it.id);
      else for (const it of filteredItems) next.add(it.id);
      return next;
    });
  };
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // Bulk acties
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`${selected.size} regel${selected.size === 1 ? '' : 's'} verwijderen?`)) return;
    let removed = 0;
    for (const id of selected) {
      try { await api.pricelists.removeItem(activeId!, id); removed++; } catch { /* skip */ }
    }
    toast.success(`${removed} regels verwijderd`);
    clearSelection();
    qc.invalidateQueries({ queryKey: ['pricelist', activeId] });
  };

  const bulkPriceUpdate = async () => {
    if (selected.size === 0) return;
    const raw = prompt(
      'Prijs-update voor ' + selected.size + ' regel(s).\n\n' +
      'Voer een waarde in:\n' +
      '  • absoluut bedrag (bv. "25" → €25)\n' +
      '  • procentuele aanpassing (bv. "+10%" of "-5%")',
    );
    if (!raw) return;
    const trimmed = raw.trim();
    const pct = trimmed.match(/^([+-]?\d+(?:[.,]\d+)?)\s*%$/);
    let updated = 0;
    if (pct) {
      const factor = 1 + parseFloat(pct[1]!.replace(',', '.')) / 100;
      for (const id of selected) {
        const item = active?.items?.find((it) => it.id === id);
        if (!item) continue;
        const nieuwePrijs = Math.round(item.prijs * factor * 100) / 100;
        try { await api.pricelists.updateItem(activeId!, id, { prijs: nieuwePrijs } as never); updated++; } catch { /* */ }
      }
    } else {
      const abs = parseFloat(trimmed.replace(',', '.'));
      if (isNaN(abs)) { toast.error('Ongeldige waarde'); return; }
      for (const id of selected) {
        try { await api.pricelists.updateItem(activeId!, id, { prijs: abs } as never); updated++; } catch { /* */ }
      }
    }
    toast.success(`${updated} prijzen aangepast`);
    clearSelection();
    qc.invalidateQueries({ queryKey: ['pricelist', activeId] });
  };

  const bulkSetNvt = async (isNvt: boolean) => {
    if (selected.size === 0) return;
    let updated = 0;
    for (const id of selected) {
      try { await api.pricelists.updateItem(activeId!, id, { isNvt } as never); updated++; } catch { /* */ }
    }
    toast.success(`${updated} regels gemarkeerd als ${isNvt ? 'NVT' : 'actief'}`);
    clearSelection();
    qc.invalidateQueries({ queryKey: ['pricelist', activeId] });
  };

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">Prijslijsten</h1>
        <p className="page-subtitle">Beheer prijzen per onderaannemer</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5 items-start">
        <div className="card-flat p-2">
          <ul className="list-none m-0 p-0 flex flex-col gap-0.5">
            {pricelists.map((p) => (
              <li key={p.id}>
                <button
                  className={`relative w-full text-left rounded-md px-3 py-2.5 transition-colors ${activeId === p.id ? 'bg-primary-50' : 'hover:bg-primary-50/60'}`}
                  onClick={() => { setActiveId(p.id); clearSelection(); }}
                  aria-current={activeId === p.id ? 'true' : undefined}
                >
                  {activeId === p.id && <span className="absolute left-0 top-1/2 h-[18px] w-[3px] -translate-y-1/2 rounded-r-full bg-accent" aria-hidden="true" />}
                  <div className="text-sm font-semibold">{p.name}</div>
                  {p.typeWerken && p.typeWerken !== 'algemeen' && (
                    <div className="text-xs font-medium text-accent-700">{p.typeWerken === 'gevel' ? 'Gevel' : 'Hellend dak'} · versie {p.versie}{p.geldigVanaf ? ` vanaf ${p.geldigVanaf}` : ''}</div>
                  )}
                  <div className="text-xs text-secondary">{p.description}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {active ? (
          <div className="card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-semibold">{active.name}</h2>
                <p className="text-sm text-secondary">{active.items?.length ?? 0} regels</p>
              </div>
              {versiebeheerd ? (
                <span className="chip-neutral" title="Versiebeheerde prijslijst">versie {active.versie}</span>
              ) : magBeheren ? (
                <button className="btn-accent btn-sm" onClick={() => setShowAdd((v) => !v)}>
                  {showAdd ? 'Annuleren' : '+ Nieuw item'}
                </button>
              ) : null}
            </div>
            {!magBeheren && (
              <div className="notice mb-4">
                Alleen-lezen: prijslijsten en standaardprijzen wijzigen kan enkel wie beheerrecht heeft (Ervin en Timen).
              </div>
            )}
            {versiebeheerd && (
              <div className="notice mb-4">
                Deze prijslijst is versiebeheerd: een versie wordt nooit aangepast, zodat bestaande calculaties niet stil veranderen. Een
                prijswijziging gebeurt via een nieuwe versie met een datum "geldig vanaf"; enkel verversen of een nieuwe calculatie gebruikt
                die. Posten met schijven:{' '}
                {(active.items ?? []).filter((it) => it.schijven).map((it) => `${it.naamArbeid} (${it.schijven!.type}: ${it.schijven!.tarieven.join(' / ')} met grenzen ${it.schijven!.grenzen.join(', ')})`).join('; ') || 'geen'}.
              </div>
            )}

            {/* Add-form */}
            {showAdd && magBeheren && <AddItemForm onSubmit={(data) => addItem.mutate(data)} />}

            {/* Filter-bar */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <input
                type="search"
                placeholder="Zoek op PDF-naam of naam arbeid…"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                aria-label="Zoek op PDF-naam of naam arbeid"
                className="input flex-1 min-w-[220px]"
              />
              <select className="select w-auto min-w-[220px]" aria-label="Naam arbeid" value={filterArbeid} onChange={(e) => setFilterArbeid(e.target.value)}>
                <option value="">Alle naam arbeid ({allArbeid.length})</option>
                {allArbeid.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <label className="flex items-center gap-2 px-1 text-sm font-medium cursor-pointer select-none">
                <input type="checkbox" checked={hideNvt} onChange={(e) => setHideNvt(e.target.checked)} />
                NVT verbergen
              </label>
              <span className="ml-auto text-sm text-secondary tabular-nums">{filteredItems.length} / {active.items?.length ?? 0}</span>
            </div>

            {/* Bulk actions */}
            {magBeheren && selected.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 bg-primary-50 rounded-md border border-primary/15">
                <span className="text-sm font-semibold">{selected.size} geselecteerd</span>
                <button className="btn-ghost btn-sm ml-auto" onClick={bulkPriceUpdate}>€ Prijs aanpassen</button>
                <button className="btn-ghost btn-sm" onClick={() => bulkSetNvt(true)}>Markeer NVT</button>
                <button className="btn-ghost btn-sm" onClick={() => bulkSetNvt(false)}>Markeer actief</button>
                <button className="btn-danger btn-sm" onClick={bulkDelete}>Verwijderen</button>
                <button className="btn-ghost btn-sm" onClick={clearSelection}>Selectie wissen</button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="thead-row">
                    {magBeheren && (
                      <th className="w-10 px-2">
                        <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} aria-label="Alles selecteren" />
                      </th>
                    )}
                    <SortTh label="Naam op opstartfiche" col="pdfNaam" current={sortKey} dir={sortDir} onSort={toggleSort} className="min-w-[260px]" />
                    <SortTh label="Eenheid" col="eenheid" current={sortKey} dir={sortDir} onSort={toggleSort} className="w-24" />
                    <SortTh label="Naam arbeid" col="naamArbeid" current={sortKey} dir={sortDir} onSort={toggleSort} className="min-w-[200px]" />
                    <SortTh label="Prijs OAN" col="prijs" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="w-28" />
                    {magBeheren && <th className="w-12"></th>}
                  </tr>
                </thead>
                <tbody>
                  {!magBeheren && filteredItems.map((it) => (
                    <tr key={it.id} className={`border-b border-lightgrey text-sm ${it.isNvt ? 'opacity-60' : ''}`}>
                      <td className="px-3 py-2">{it.pdfNaam}</td>
                      <td className="px-3 py-2">{it.eenheid}</td>
                      <td className="px-3 py-2">{it.naamArbeid}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtEUR(it.prijs)}</td>
                    </tr>
                  ))}
                  {magBeheren && filteredItems.map((it) => (
                    <tr key={it.id} className={`border-b border-lightgrey ${it.isNvt ? 'opacity-60' : ''} ${selected.has(it.id) ? 'bg-primary-50' : ''}`}>
                      <td className="px-2 py-1">
                        <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleOne(it.id)} />
                      </td>
                      <td className="px-3 py-1">
                        <input
                          className="w-full bg-transparent text-sm py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                          defaultValue={it.pdfNaam}
                          key={it.id + '-pdf-' + it.pdfNaam}
                          onBlur={(e) => { if (e.target.value !== it.pdfNaam) updateItem.mutate({ itemId: it.id, data: { pdfNaam: e.target.value } }); }}
                        />
                      </td>
                      <td className="px-3 py-1">
                        <select
                          className="text-sm py-1 bg-transparent border border-transparent hover:border-border rounded-sm"
                          defaultValue={it.eenheid}
                          key={it.id + '-eenheid-' + it.eenheid}
                          onChange={(e) => updateItem.mutate({ itemId: it.id, data: { eenheid: e.target.value } })}
                        >
                          {EENHEDEN.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-1">
                        <input
                          className="w-full bg-transparent text-sm py-1 hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                          defaultValue={it.naamArbeid}
                          key={it.id + '-arb-' + it.naamArbeid}
                          onBlur={(e) => { if (e.target.value !== it.naamArbeid) updateItem.mutate({ itemId: it.id, data: { naamArbeid: e.target.value } }); }}
                        />
                      </td>
                      <td className="px-3 py-1 text-right">
                        <input
                          type="number"
                          step="0.01"
                          className="w-full text-right bg-transparent text-sm py-1 tabular-nums hover:bg-white focus:bg-white focus:outline-1 focus:outline-primary rounded-sm"
                          defaultValue={it.prijs}
                          key={it.id + '-prijs-' + it.prijs}
                          onBlur={(e) => {
                            const n = parseFloat(e.target.value);
                            if (n !== it.prijs) updateItem.mutate({ itemId: it.id, data: { prijs: isNaN(n) ? 0 : n } });
                          }}
                        />
                        <div className="text-[11px] text-secondary text-right tabular-nums">{fmtEUR(it.prijs)}</div>
                      </td>
                      <td className="px-3 py-1">
                        <button className="btn-ghost btn-sm px-2" onClick={() => { if (confirm('Regel verwijderen?')) removeItem.mutate(it.id); }} title="Verwijder regel" aria-label="Verwijder regel"><Icon name="sluiten" size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredItems.length === 0 && (
                <p className="text-center text-sm text-secondary py-8">Geen regels gevonden met de huidige filters.</p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-secondary">Selecteer een prijslijst.</p>
        )}
      </div>
    </AppShell>
  );
}

interface SortThProps {
  label: string;
  col: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (col: SortKey) => void;
  align?: 'left' | 'right';
  className?: string;
}

function SortTh({ label, col, current, dir, onSort, align = 'left', className = '' }: SortThProps) {
  const isActive = current === col;
  return (
    <th
      className={`px-3 py-2 cursor-pointer select-none hover:text-primary ${isActive ? 'text-primary' : ''} ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
      onClick={() => onSort(col)}
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {isActive && <Icon name="chevron" size={14} strokeWidth={2.25} className={dir === 'asc' ? 'rotate-180' : ''} />}
      </span>
    </th>
  );
}

interface AddItemFormProps {
  onSubmit: (data: Omit<PricelistItem, 'id' | 'pricelistId'>) => void;
}

function AddItemForm({ onSubmit }: AddItemFormProps) {
  const [pdfNaam, setPdfNaam] = useState('');
  const [naamArbeid, setNaamArbeid] = useState('');
  const [eenheid, setEenheid] = useState<typeof EENHEDEN[number]>('STUK');
  const [prijs, setPrijs] = useState(0);
  const [isNvt, setIsNvt] = useState(false);

  const submit = () => {
    if (!pdfNaam.trim() || !naamArbeid.trim()) {
      alert('PDF-naam en Naam arbeid zijn verplicht.');
      return;
    }
    onSubmit({
      pdfNaam: pdfNaam.toUpperCase(),
      naamArbeid: naamArbeid.toUpperCase(),
      eenheid,
      prijs,
      isNvt,
      sortOrder: 0,
    });
    setPdfNaam(''); setNaamArbeid(''); setEenheid('STUK'); setPrijs(0); setIsNvt(false);
  };

  return (
    <div className="mb-4 rounded-md border border-border bg-canvas p-4">
      <h3 className="text-sm font-semibold mb-3">Nieuw item toevoegen</h3>
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_2fr_1fr_auto] gap-3 items-end">
        <div>
          <label htmlFor="nieuw-item-pdf" className="field-label">Naam op opstartfiche</label>
          <input
            id="nieuw-item-pdf"
            className="input"
            placeholder="bv. KORAMIC ACTUA 10"
            value={pdfNaam}
            onChange={(e) => setPdfNaam(e.target.value.toUpperCase())}
          />
        </div>
        <div>
          <label htmlFor="nieuw-item-eenheid" className="field-label">Eenheid</label>
          <select id="nieuw-item-eenheid" className="select" value={eenheid} onChange={(e) => setEenheid(e.target.value as typeof EENHEDEN[number])}>
            {EENHEDEN.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="nieuw-item-arbeid" className="field-label">Naam arbeid (groep)</label>
          <input
            id="nieuw-item-arbeid"
            className="input"
            placeholder="bv. NETTO DAK"
            value={naamArbeid}
            onChange={(e) => setNaamArbeid(e.target.value.toUpperCase())}
          />
        </div>
        <div>
          <label htmlFor="nieuw-item-prijs" className="field-label">Prijs (€)</label>
          <input
            id="nieuw-item-prijs"
            type="number"
            step="0.01"
            min="0"
            className="input text-right tabular-nums"
            value={prijs}
            onChange={(e) => setPrijs(parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
            <input type="checkbox" checked={isNvt} onChange={(e) => setIsNvt(e.target.checked)} />
            NVT
          </label>
          <button className="btn-primary btn-sm" onClick={submit}>+ Toevoegen</button>
        </div>
      </div>
    </div>
  );
}
